import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { defaultAvatar } from "@/lib/defaults";
import { fmtMoney } from "@/lib/payments";
import { DONATION_SENT_EVENT } from "@/lib/donationEvents";
import { imageVariant } from "@/lib/mediaUrl";

// Relayé par NotificationBell quand une notification « donation_received » arrive en temps réel.
export const DONATION_RECEIVED_EVENT = "yalola:donation-received";
// Interrupteur admin (Paramètres › Modules activés) — activé par défaut.
export const thanksOverlayEnabled = (config) => config?.settings?.features?.donation_thanks_overlay !== false;
const SHOW_MS = 7000;
const HEARTS = ["❤️", "💝", "❤️", "🧡", "❤️", "💛", "🙏"];

// Pluie de cœurs plein écran, sans interaction — aussi utilisée sur la page « Merci ! » du donateur.
export const HeartRain = ({ count = 14, durationMs = 2600, testId = "heart-rain" }) => {
  const [hearts] = useState(() => Array.from({ length: count }, (_, i) => ({
    id: i,
    emoji: HEARTS[i % HEARTS.length],
    delay: Math.round((i / count) * durationMs),
    left: `${8 + Math.round(Math.random() * 84)}%`,
    drift: Math.round((Math.random() * 2 - 1) * 60),
    rot: Math.round((Math.random() * 2 - 1) * 30),
    size: 26 + Math.round(Math.random() * 18),
  })));
  return createPortal(
    <div className="fixed inset-0 pointer-events-none z-[124]" aria-hidden="true" data-testid={testId}>
      {hearts.map((h) => (
        <span
          key={h.id}
          className="live-float absolute select-none"
          style={{ left: h.left, bottom: "calc(var(--sa-bottom, 0px) + 6rem)", fontSize: `${h.size}px`, animationDelay: `${h.delay}ms`, animationFillMode: "both", "--drift": `${h.drift}px`, "--rot": `${h.rot}deg` }}
        >{h.emoji}</span>
      ))}
    </div>,
    document.body
  );
};

const toItem = (n) => ({
  id: n.id,
  actorId: n.actor_id || n.actor?.id || "",
  name: n.actor?.name || "",
  avatar: n.actor?.avatar_url ? imageVariant(n.actor.avatar_url, 48) : defaultAvatar(n.actor?.gender),
  amount: n.meta?.amount,
  currency: n.meta?.currency || "EUR",
  message: (n.meta?.message || "").trim(),
});

// « Merci animé » côté bénéficiaire : cœurs qui montent + carte (avatar, « Marie te soutient · 20 € », message),
// partout dans l'app, 7 s, tap → profil du donateur. Rejoue à l'ouverture les soutiens reçus hors ligne (max 3).
export const DonationThanksOverlay = () => {
  const { user, t, lang, config } = useApp();
  const enabled = thanksOverlayEnabled(config);
  const nav = useNavigate();
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!user || !enabled) return undefined;
    const onEvt = (e) => {
      const n = e?.detail;
      if (!n?.id) return;
      setQueue((q) => (q.some((x) => x.id === n.id) ? q : [...q, toItem(n)]));
    };
    window.addEventListener(DONATION_RECEIVED_EVENT, onEvt);
    return () => window.removeEventListener(DONATION_RECEIVED_EVENT, onEvt);
  }, [user, enabled]);

  useEffect(() => {
    if (!user?.id || !enabled) return;
    api.get("/notifications/thanks/pending").then(({ data }) => {
      const items = (data?.items || []).map(toItem).reverse();
      if (items.length) setQueue((q) => [...q, ...items.filter((i) => !q.some((x) => x.id === i.id))]);
    }).catch(() => {});
  }, [user?.id, enabled]);

  useEffect(() => {
    if (current || queue.length === 0) return;
    const [next, ...rest] = queue;
    setQueue(rest);
    setCurrent(next);
    api.post("/notifications/thanks/seen", { ids: [next.id] }).catch(() => {});
    timer.current = setTimeout(() => setCurrent(null), SHOW_MS);
  }, [queue, current]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const dismiss = useCallback(() => { clearTimeout(timer.current); setCurrent(null); }, []);

  if (!current || !user || !enabled) return null;
  const title = t("thanks.title", "{name} te soutient · {amount}")
    .replace("{name}", current.name || t("thanks.someone", "Un membre"))
    .replace("{amount}", fmtMoney(current.amount, current.currency, lang));
  const goProfile = () => { dismiss(); if (current.actorId) nav(`/profile/${current.actorId}`); };

  return (
    <>
      <HeartRain key={current.id} testId="thanks-heart-rain" />
      {createPortal(
        <div className="fixed inset-x-0 z-[126] flex justify-center px-4 pointer-events-none" style={{ top: "calc(var(--sa-top, 0px) + 0.75rem)" }} data-testid="donation-thanks-overlay">
          <div
            role="status"
            className="thanks-card pointer-events-auto w-full max-w-sm rounded-2xl shadow-xl border p-3 flex gap-3 items-start cursor-pointer"
            style={{ background: "var(--yalola-surface)", borderColor: "var(--yalola-border)" }}
            onClick={goProfile}
            data-testid="donation-thanks-card"
          >
            <img src={current.avatar} alt="" className="h-11 w-11 rounded-full object-cover shrink-0" style={{ boxShadow: "0 0 0 2px var(--yalola-primary)" }} />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-primary)" }}>{t("thanks.kicker", "💝 Nouveau soutien")}</p>
              <p className="font-bold text-sm leading-snug truncate" data-testid="donation-thanks-title">{title}</p>
              {current.message ? (
                <p className="text-sm italic mt-0.5 line-clamp-3" style={{ color: "var(--yalola-text-muted)" }} data-testid="donation-thanks-message">« {current.message} »</p>
              ) : null}
              <p className="text-xs mt-1 underline" style={{ color: "var(--yalola-text-muted)" }}>{t("thanks.view_profile", "Voir le profil")}</p>
            </div>
            <button
              type="button"
              aria-label={t("common.close", "Fermer")}
              onClick={(e) => { e.stopPropagation(); dismiss(); }}
              className="p-1 rounded-full hover:bg-black/5 shrink-0"
              data-testid="donation-thanks-close"
            ><X size={16} /></button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// « Merci ! » du DONATEUR après un paiement natif confirmé : cœurs + carte 4 s, on reste où l'on est (direct, profil).
// Remplace l'ancienne page « Merci » plein écran qui sortait la personne du direct.
const DONOR_SHOW_MS = 4000;
const donorCelebrated = new Set(); // paiements déjà fêtés (statut relu + notification = une seule fête)
export const DonorThanksOverlay = () => {
  const { t, lang } = useApp();
  const [current, setCurrent] = useState(null);
  const timer = useRef(null);
  useEffect(() => {
    const onEvt = (e) => {
      const d = e?.detail || {};
      if (d.id && donorCelebrated.has(d.id)) return;
      if (d.id) donorCelebrated.add(d.id);
      clearTimeout(timer.current);
      setCurrent({ id: d.id || `${Date.now()}`, amount: d.amount, currency: d.currency || "EUR", body: d.body || null });
      timer.current = setTimeout(() => setCurrent(null), DONOR_SHOW_MS);
    };
    window.addEventListener(DONATION_SENT_EVENT, onEvt);
    return () => { window.removeEventListener(DONATION_SENT_EVENT, onEvt); clearTimeout(timer.current); };
  }, []);
  if (!current) return null;
  const amount = current.amount > 0 ? fmtMoney(current.amount, current.currency, lang) : "";
  const title = amount
    ? t("donate.sent_title", "Merci ! Ton don de {amount} a été reçu").replace("{amount}", amount)
    : t("donate.sent_title_plain", "Merci ! Ton don a été reçu");
  return (
    <>
      <HeartRain key={current.id} testId="donor-heart-rain" />
      {createPortal(
        <div className="fixed inset-x-0 z-[126] flex justify-center px-4 pointer-events-none" style={{ top: "calc(var(--sa-top, 0px) + 0.75rem)" }} data-testid="donor-thanks-overlay">
          <div role="status" className="thanks-card pointer-events-auto w-full max-w-sm rounded-2xl shadow-xl border p-3 flex gap-3 items-center"
               style={{ background: "var(--yalola-surface)", borderColor: "var(--yalola-border)" }} onClick={() => setCurrent(null)} data-testid="donor-thanks-card">
            <span className="text-2xl shrink-0" aria-hidden="true">🙏</span>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-sm leading-snug" data-testid="donor-thanks-title">{title}</p>
              <p className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }} data-testid="donor-thanks-body">{current.body || t("donate.sent_body", "Que Dieu te bénisse !")}</p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default DonationThanksOverlay;
